import { supabase } from "~/integrations/supabase/client";
import { SalesRequest } from "~/types/client";
import { chunkArray } from "~/utils/arrayUtils";

export interface ClientInfo {
  name: string;
  isDeleted: boolean;
  taxId?: string;
  salesConditionName?: string;
  salesConditionType?: string;
  fiscalAddress?: {
    street: string;
    number: string;
    city: string;
    comuna?: string;
    state?: string;
  };
}

export const fetchClientNamesUtil = async (
  requests: SalesRequest[]
): Promise<Record<string, string>> => {
  try {
    const clientIds = [...new Set(requests.map((req) => req.clientId))];

    // Batch the requests to avoid URL length limits
    const batches = chunkArray(clientIds, 100); // Process 100 IDs at a time
    const nameMap: Record<string, string> = {};

    for (const batch of batches) {
      const { data: clientsData, error: clientsError } = await supabase
        .from("clients")
        .select("id, name, deleted_at")
        .in("id", batch); // Include both active and deleted clients

      if (clientsError) throw clientsError;

      if (clientsData) {
        clientsData.forEach((client) => {
          nameMap[client.id] = client.name || "Cliente sin nombre";
        });
      }
    }

    return nameMap;
  } catch (error) {
    console.error("Error fetching client names:", error);
    return {};
  }
};

export const fetchClientInfoUtil = async (
  requests: SalesRequest[]
): Promise<Record<string, ClientInfo>> => {
  try {
    const clientIds = [...new Set(requests.map((req) => req.clientId))];

    // Use direct table queries instead of RPC function
    const { data: clientsData, error: clientsError } = await supabase
      .from("clients")
      .select(
        `
        id,
        name,
        deleted_at,
        tax_id,
        sales_condition_id,
        fiscal_address_id,
        addresses:fiscal_address_id(street, number, city, comuna, state)
      `
      )
      .in("id", clientIds);

    if (clientsError) {
      throw clientsError;
    }

    // Now fetch sales conditions separately
    const salesConditionIds = clientsData
      ?.map((client: any) => client.sales_condition_id)
      .filter(Boolean);

    let salesConditionsData: any[] = [];
    if (salesConditionIds && salesConditionIds.length > 0) {
      const { data: conditionsData, error: conditionsError } = await supabase
        .from("sales_conditions")
        .select("id, name, type")
        .in("id", salesConditionIds);

      if (conditionsError) {
        console.error(
          "fetchClientInfoUtil - conditionsError:",
          conditionsError
        );
      } else {
        salesConditionsData = conditionsData || [];
      }
    }

    if (clientsError) {
      throw clientsError;
    }

    const clientInfoMap: Record<string, ClientInfo> = {};

    if (clientsData) {
      clientsData.forEach((client: any) => {
        // Find the sales condition for this client
        const salesCondition = salesConditionsData.find(
          (condition: any) => condition.id === client.sales_condition_id
        );

        // Get fiscal address data from the joined addresses table
        const fiscalAddress =
          client.addresses && typeof client.addresses === "object"
            ? (client.addresses as any)
            : null;

        clientInfoMap[client.id] = {
          name: client.name || "Cliente sin nombre",
          isDeleted: !!client.deleted_at,
          taxId: client.tax_id || undefined,
          salesConditionName: salesCondition?.name || undefined,
          salesConditionType: salesCondition?.type || undefined,
          fiscalAddress: fiscalAddress
            ? {
                street: fiscalAddress.street || "",
                number: fiscalAddress.number || "",
                city: fiscalAddress.city || "",
                comuna: fiscalAddress.comuna || undefined,
                state: fiscalAddress.state || undefined,
              }
            : undefined,
        };
      });
    }

    return clientInfoMap;
  } catch (error) {
    console.error("Error fetching client info:", error);
    return {};
  }
};
